import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getMe } from "@elation/api-client/src/stitch-web";

import { LoginCallbackPage } from "@src/deprecated/pages/LoginCallback";
import {
  getDefaultPostAuthSuccessDestination,
  resolvePostAuthDestination,
} from "@src/features/auth";
import { navigation } from "@src/shared/navigation";
import { toErrorMessage } from "@src/shared/utils/errors";
import { events } from "@src/shared/utils/observability/events";
import {
  trackSessionInitFailure,
  trackUserLoggedIn,
} from "@src/shared/utils/observability/trackUserLoggedIn";

export function meta() {
  return [
    { title: "Signing In..." },
    { name: "description", content: "Completing authentication" },
  ];
}

/**
 * Post-login landing page after Better Auth Okta OAuth.
 * The session cookie is set by `/api/auth/callback/okta` before the user arrives here.
 */
export default function LoginCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [postCallbackError, setPostCallbackError] = useState<string | null>(
    null,
  );

  const error = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");
  const redirectTo =
    searchParams.get("redirectTo") || getDefaultPostAuthSuccessDestination();

  useEffect(() => {
    if (error) {
      events.loginFailed.track({
        method: "okta_web",
        error: errorDescription ?? error,
        error_code: error,
      });
      setIsProcessing(false);
      return;
    }

    const completeLogin = async () => {
      try {
        const [{ data: me }, destination] = await Promise.all([
          getMe(),
          resolvePostAuthDestination(redirectTo, {
            route: "login/callback",
            operation: "oktaCallback",
          }),
        ]);
        trackUserLoggedIn(me, "okta_web");
        navigate(destination, { replace: true });
      } catch (err) {
        console.error("Error fetching /me after OAuth callback:", err);
        setPostCallbackError(
          toErrorMessage(err, "Sign-in failed after OAuth callback"),
        );
        trackSessionInitFailure("okta_web", err);
        setIsProcessing(false);
      }
    };

    void completeLogin();
  }, [error, errorDescription, redirectTo, navigate]);

  const handleRetry = () => {
    navigate(navigation.login(), { replace: true });
  };

  return (
    <LoginCallbackPage
      error={error ?? postCallbackError ?? undefined}
      errorDescription={errorDescription || undefined}
      isLoading={isProcessing}
      onRetry={handleRetry}
    />
  );
}
